import { useState } from 'react';
import { useApp, useFetch } from '../store/app';
import { Fade, BulkBar, BulkAction, CheckBox } from './common';
import { Icon } from '../components/Icon';
import { Card, Button, Avatar } from '../components/primitives';
import { SideModal, LabeledText, LabeledArea } from '../components/SideModal';
import { av } from '../lib/avatar';
import { api } from '../lib/api';
import { can } from '../lib/rbac';
import { useSelection } from '../lib/useSelection';
import type { Person, Team } from '../../shared/types';

const TEAM_COLS = '30px 1.6fr 2fr 1.4fr 40px';

export function Teams() {
  const { refresh, user } = useApp();
  const { data: teams } = useFetch<Team[]>('/teams');
  const { data: people } = useFetch<Person[]>('/people');
  const [editing, setEditing] = useState<Team | 'new' | null>(null);
  const canManage = !!user && (user.role === 'super_admin' || user.role === 'admin');
  const staff = people || [];
  const byId = (pid: number) => staff.find((p) => p.id === pid);
  const sel = useSelection((teams || []).map((t) => t.id));

  const bulkDelete = async () => {
    if (!confirm(`Delete ${sel.count} team${sel.count === 1 ? '' : 's'}?`)) return;
    await api.post('/teams/bulk', { action: 'delete', ids: sel.ids });
    sel.clear();
    refresh();
  };

  return (
    <Fade>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Group staff into teams to target them as a unit when assigning work.</div>
        <div style={{ flex: 1 }} />
        {canManage && <Button icon="add" onClick={() => setEditing('new')}>New team</Button>}
      </div>

      <Card style={{ overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: TEAM_COLS, gap: 14, padding: '10px 18px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em', alignItems: 'center' }}>
          <div>{canManage ? <CheckBox checked={sel.allChecked} indeterminate={sel.someChecked} onChange={sel.toggleAll} /> : null}</div>
          <div>Team</div><div>Description</div><div>Members</div><div />
        </div>
        {(teams || []).map((t) => (
          <div key={t.id} onClick={canManage ? () => setEditing(t) : undefined} className={canManage ? 'cc-hover-s2 cc-clickable' : undefined} style={{ display: 'grid', gridTemplateColumns: TEAM_COLS, gap: 14, padding: '12px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center', background: sel.has(t.id) ? 'var(--accent-bg)' : undefined }}>
            <div>{canManage ? <CheckBox checked={sel.has(t.id)} onChange={() => sel.toggle(t.id)} /> : null}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <Icon name="diversity_3" size={18} color="var(--accent)" />
              <span style={{ fontWeight: 600, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description || '—'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="cc-mono" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-3)', width: 20 }}>{t.member_count ?? t.member_ids.length}</span>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {t.member_ids.slice(0, 6).map((pid) => {
                  const p = byId(pid);
                  if (!p) return null;
                  const a = av(p.name, p.avatar_kind);
                  return <span key={pid} title={p.name} style={{ marginRight: -7 }}><Avatar initials={a.in} bg={a.bg} fg={a.fg} size={26} ring /></span>;
                })}
                {t.member_ids.length > 6 && <span style={{ marginLeft: 12, fontSize: 11.5, color: 'var(--text-3)' }}>+{t.member_ids.length - 6}</span>}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              {canManage && <span onClick={(e) => { e.stopPropagation(); setEditing(t); }} style={{ cursor: 'pointer', display: 'inline-flex' }}><Icon name="edit" size={18} color="var(--text-3)" /></span>}
            </div>
          </div>
        ))}
        {(teams || []).length === 0 && <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No teams yet.</div>}
      </Card>

      {canManage && (
        <BulkBar count={sel.count} onClear={sel.clear}>
          <BulkAction label="Delete" icon="delete" danger onClick={bulkDelete} />
        </BulkBar>
      )}

      {editing && <TeamEditor team={editing === 'new' ? null : editing} staff={staff} onClose={() => setEditing(null)} onSaved={() => { refresh(); setEditing(null); }} />}
    </Fade>
  );
}

function TeamEditor({ team, staff, onClose, onSaved }: { team: Team | null; staff: Person[]; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(team?.name || '');
  const [description, setDescription] = useState(team?.description || '');
  const [members, setMembers] = useState<number[]>(team?.member_ids || []);
  const [busy, setBusy] = useState(false);

  const toggle = (id: number) => setMembers((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { name, description, member_ids: members };
      if (team) await api.put(`/teams/${team.id}`, payload);
      else await api.post('/teams', payload);
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  const del = async () => {
    if (!team || !confirm(`Delete team "${team.name}"?`)) return;
    await api.del(`/teams/${team.id}`);
    onSaved();
  };

  return (
    <SideModal
      title={team ? 'Edit team' : 'New team'}
      sub="Name the team and pick its members"
      onClose={onClose}
      footer={
        <>
          {team ? <Button variant="danger" onClick={del}>Delete</Button> : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy || !name}>{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </>
      }
    >
      <LabeledText label="Team name" value={name} onChange={setName} placeholder="e.g. PREA Audit Team" />
      <LabeledArea label="Description" value={description} onChange={setDescription} rows={2} placeholder="What this team handles" />
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em', margin: '4px 0 8px' }}>Members ({members.length})</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {staff.map((p) => {
          const a = av(p.name, p.avatar_kind);
          const on = members.includes(p.id);
          return (
            <div key={p.id} onClick={() => toggle(p.id)} className="cc-clickable" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: on ? 'var(--accent-bg)' : 'transparent' }}>
              <Avatar initials={a.in} bg={a.bg} fg={a.fg} size={30} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{p.role} · {[p.city, p.state].filter(Boolean).join(', ')}</div>
              </div>
              <Icon name={on ? 'check_circle' : 'radio_button_unchecked'} size={20} fill={on ? 1 : 0} color={on ? 'var(--accent)' : 'var(--text-3)'} />
            </div>
          );
        })}
      </div>
    </SideModal>
  );
}
